import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { getAllAnnotators } from "services/userService";
import { reassignPendingSamples, getAnnotatorSampleCount } from "services/sampleService";
import { getUserInfo } from "services/authService";
import AppSnackbar from "layouts/notifications/Snackbar";

export default function ReassignSamples({ open, onClose, user, onSuccess }) {
  const user_id = user ? user.id : null;

  const [annotatorList, setAnnotatorData] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedValue, setSelectedValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });
  const showSnackbar = (type, message) => setSnackbar({ open: true, type, message });
  const closeSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const adminId = getUserInfo()?.id;

        // Only annotators created by the logged-in admin, excluding this annotator.
        const data = await getAllAnnotators();
        const targets = data.filter((a) => Number(a.created_by) === adminId && a.id !== user_id);
        setAnnotatorData(targets);

        // Count of not-started (Pending) samples for this annotator.
        const { data_summary } = await getAnnotatorSampleCount(user_id);
        const pending = (data_summary || [])
          .filter((row) => row.status === "Pending")
          .reduce((sum, row) => sum + Number(row.count || 0), 0);
        setPendingCount(pending);
      } catch (error) {
        console.error("Failed to load reassign data", error);
      } finally {
        setLoading(false);
      }
    };

    if (open && user_id) {
      fetchData();
    } else {
      setSelectedValue("");
      setPendingCount(0);
    }
  }, [open, user_id]);

  const hasTarget = annotatorList.length > 0;
  const canReassign = pendingCount > 0 && hasTarget;

  const handleCancel = () => onClose();

  const onProceed = async () => {
    if (!selectedValue) {
      showSnackbar("warning", "Please select annotator from dropdown");
      return;
    }
    try {
      const result = await reassignPendingSamples(user_id, selectedValue);
      if (result.status === "success") {
        showSnackbar("success", result.message || "Samples reassigned successfully");
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      console.error("Failed to reassign samples", error);
      showSnackbar("error", "Failed to reassign samples");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">Reassign Samples</MDTypography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {loading ? (
            <DialogContent sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
              <MDTypography sx={{ fontSize: "16px", color: "black" }}>Loading...</MDTypography>
            </DialogContent>
          ) : !canReassign ? (
            <DialogContent id="no_reassign_dialog" sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
              <DialogContent sx={{ p: 0, mb: 2 }}>
                <MDTypography sx={{ fontSize: "16px", color: "black" }}>
                  {pendingCount === 0
                    ? "This annotator has no pending(un-started) samples to reassign."
                    : "There is no other annotator available to reassign these samples to. Please add another annotator first."}
                </MDTypography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    color: "#6c757d",
                    borderColor: "#dee2e6",
                    textTransform: "none",
                    "&:hover": { borderColor: "#ced4da" },
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </DialogContent>
          ) : (
            <DialogContent id="reassign_dialog" sx={{ backgroundColor: "#f8f9fa", p: 2 }}>
              <DialogContent sx={{ p: 0, mb: 1 }}>
                <MDTypography py={2} sx={{ fontSize: "16px", color: "black" }}>
                  Reassign {pendingCount} pending(un-started) sample{pendingCount > 1 ? "s" : ""} to
                </MDTypography>
                <Box width={300}>
                  <FormControl fullWidth>
                    <Select
                      value={selectedValue}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      displayEmpty
                      fullWidth
                      sx={{ height: 40, px: 1 }}
                      onOpen={() => setIsDropdownOpen(true)}
                      onClose={() => setIsDropdownOpen(false)}
                      IconComponent={() => (
                        <FontAwesomeIcon icon={isDropdownOpen ? faChevronUp : faChevronDown} />
                      )}
                    >
                      <MenuItem value=""> Select Annotator </MenuItem>
                      {annotatorList.map((option, idx) => (
                        <MenuItem key={idx} value={option.id}>
                          {option.first_name + " " + option.last_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={onProceed}
                  variant="contained"
                  sx={{
                    backgroundColor: "#17a2b8",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#138496" },
                  }}
                >
                  Proceed
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  sx={{
                    color: "#6c757d",
                    borderColor: "#dee2e6",
                    textTransform: "none",
                    "&:hover": { borderColor: "#ced4da" },
                  }}
                >
                  Discard
                </Button>
              </DialogActions>
            </DialogContent>
          )}
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

ReassignSamples.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onSuccess: PropTypes.func,
};
